import { useEffect, useCallback, useState } from 'react';

import * as Sentry from '@sentry/browser';
import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { useRouterActions, useQueryParam } from 'react-resource-router';

import { OptionType } from '@atlaskit/select';

import {
  getBranchViewDestinationRepoName,
  getBranchViewDestinationBranchName,
  getDefaultDestBranchName,
  BranchSelectorType,
  CHANGE_DEST_BRANCH,
} from 'src/redux/repository/branch/branch-redux';
import { Ref } from 'src/sections/create-branch';
import { LOADING_STATE } from 'src/sections/create-branch/constants';
import { getRef, getRefs } from 'src/sections/create-branch/utils';
import {
  generateRefUrl,
  fetchRefOptions as helperFetchRefOptions,
} from 'src/sections/repository/components/ref-selector/utils';
import {
  getRepositoryDetails,
  getRepositoryMainBranch,
  getCurrentRepositoryFullSlug,
  getRepositoryBranchingModel,
} from 'src/selectors/repository-selectors';

export const useRefSelectorActions = () => {
  const dispatch = useDispatch();
  const [destParam] = useQueryParam('dest');

  const { push } = useRouterActions();

  const { parent } = useSelector(getRepositoryDetails);

  const destRepoName = useSelector(getBranchViewDestinationRepoName);
  const destBranchName = useSelector(getBranchViewDestinationBranchName) || '';
  const defaultDestBranchName = useSelector(getDefaultDestBranchName) || '';
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const mainBranch = useSelector(getRepositoryMainBranch);
  const branchingModel = useSelector(getRepositoryBranchingModel);

  const branchSelector: BranchSelectorType = {
    loadingState: LOADING_STATE.LOADING,
    refs: [],
    hasMoreRefs: false,
  };

  const [state, setState] = useState(branchSelector);

  const getMainBranchFromRepoName = useCallback(
    (repoName: string) => {
      if (repoName === parent?.full_name) {
        return parent.mainbranch;
      }
      return mainBranch;
    },

    [mainBranch, parent?.mainbranch, parent?.full_name]
  );

  const onChangeToBranch = (branch: Ref) => {
    const queryString = parent ? `${destRepoName}:${branch.name}` : branch.name;

    push(`?${qs.stringify({ dest: queryString })}`);
  };

  const onChangeToRepository = (repoOption: OptionType) => {
    const destRepoMBName = getMainBranchFromRepoName(
      repoOption.value as string
    )?.name;
    const queryString =
      parent && destRepoMBName
        ? `${repoOption.value}:${destRepoMBName}`
        : mainBranch?.name;

    push(`?${qs.stringify({ dest: queryString })}`);
  };

  const fetchRefOptions = useCallback(
    async (payload: { search?: string; exact?: boolean } = {}) => {
      if (!destRepoName) {
        return;
      }

      const { search = '', exact = false } = payload;
      const url = generateRefUrl(
        destRepoName,
        search,
        'branch',
        getMainBranchFromRepoName(destRepoName),
        branchingModel,
        '-target.date',
        { type: 'exact', exact }
      );

      try {
        const { preparedRefs, next } = await helperFetchRefOptions(
          url,
          true,
          search,
          'branch',
          getMainBranchFromRepoName(destRepoName),
          branchingModel
        );

        setState(s => ({
          ...s,
          refs: getRefs(preparedRefs),
          hasMoreRefs: !!next,
          loadingState: LOADING_STATE.SUCCESS,
        }));
      } catch {
        setState(prevState => ({
          ...prevState,
          loadingState: LOADING_STATE.ERROR,
        }));
      }
    },
    [destRepoName, branchingModel, getMainBranchFromRepoName]
  );

  const parseDestQueryParam = useCallback(
    (dest: string | undefined): string[] => {
      if (dest && dest.includes(':')) {
        return dest.split(':', 2);
      } else if (dest) {
        return [repositoryFullSlug, dest];
      }

      // if there are no query params then the destination
      // is in the current repo
      return [repositoryFullSlug, defaultDestBranchName];
    },
    [repositoryFullSlug, defaultDestBranchName]
  );

  // handles updating dest repo/branch when query param changes
  // due to history navigation or user selection
  useEffect(
    () => {
      if (!destRepoName || !destBranchName) {
        return;
      }

      const [repo, branch] = parseDestQueryParam(destParam);

      const refetchRefOptions = () => {
        const url = generateRefUrl(
          repo,
          branch,
          'branch',
          mainBranch,
          branchingModel,
          '-target.date',
          { type: 'exact', exact: true }
        );

        setState(s => ({
          ...s,
          loadingState: LOADING_STATE.LOADING,
        }));

        // search for exact match
        helperFetchRefOptions(
          url,
          false,
          branch,
          'branch',
          mainBranch,
          branchingModel
        ).then(res => {
          if (res.preparedRefs.length === 0) {
            Sentry.captureException(
              'Failed to change branch view destination branch'
            );
            setState(s => ({
              ...s,
              loadingState: LOADING_STATE.ERROR,
            }));
          } else {
            // first match is exact match
            dispatch({
              type: CHANGE_DEST_BRANCH,
              payload: {
                ref: getRef(res.preparedRefs[0]),
                repo,
              },
            });

            setState(s => ({
              ...s,
              loadingState: LOADING_STATE.SUCCESS,
            }));
          }
        });
      };

      // handles changing destination repo (implies destination branch
      // also changes)
      if (repo !== destRepoName) {
        refetchRefOptions();
      }

      // handles changing branch ONLY
      else if (branch !== destBranchName) {
        if (!(state.loadingState === LOADING_STATE.LOADING)) {
          const selectedBranch = state.refs.find(
            refName => refName.name === branch
          );

          // selected branch is in the current page of results
          if (selectedBranch) {
            dispatch({
              type: CHANGE_DEST_BRANCH,
              payload: {
                ref: selectedBranch,
                repo,
              },
            });
          } else {
            // selected branch is NOT in the first page of results
            refetchRefOptions();
          }
        }
      }

      // fetch options for branch selector on initial load
      else {
        fetchRefOptions();
      }
    },

    // only refetch when dest changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, destParam, destRepoName]
  );

  return { state, fetchRefOptions, onChangeToBranch, onChangeToRepository };
};
